import { useState } from 'react';

interface GuidanceInitiative {
  id: string;
  code: string;
  title: string;
  type: 'Guideline' | 'Advisory' | 'Letter' | 'Framework';
  status: 'On Track' | 'Behind' | 'Blocked' | 'Published';
  dueDate: string;
  publishedDate?: string;
  priority: 'High' | 'Medium' | 'Low';
  progress: number;
  owner: string;
  description: string;
  milestones: {
    name: string;
    date: string;
    completed: boolean;
  }[];
}

const guidanceInitiatives: GuidanceInitiative[] = [
  {
    id: 'GI-001',
    code: 'E-23',
    title: 'Model Risk Management Revision',
    type: 'Guideline',
    status: 'Published',
    dueDate: '2027-05-01',
    publishedDate: '2025-03-15',
    priority: 'High',
    progress: 100,
    owner: 'Model Risk Team',
    description: 'Revision to incorporate AI/ML model governance requirements',
    milestones: [
      { name: 'Draft consultation', date: '2024-03-22', completed: true },
      { name: 'Stakeholder feedback', date: '2024-08-15', completed: true },
      { name: 'Final publication', date: '2025-03-15', completed: true },
      { name: 'Effective date', date: '2027-05-01', completed: false },
    ],
  },
  {
    id: 'GI-002',
    code: 'B-15',
    title: 'Climate Risk Management Update',
    type: 'Guideline',
    status: 'Published',
    dueDate: '2025-03-07',
    publishedDate: '2025-03-07',
    priority: 'High',
    progress: 100,
    owner: 'Climate Risk Team',
    description: 'Updated guidance on climate risk assessment and disclosure',
    milestones: [
      { name: 'Initial draft', date: '2024-06-01', completed: true },
      { name: 'Public consultation', date: '2024-09-30', completed: true },
      { name: 'Final publication', date: '2025-03-07', completed: true },
    ],
  },
  {
    id: 'GI-003',
    code: 'B-20',
    title: 'Cybersecurity Incident Reporting',
    type: 'Guideline',
    status: 'On Track',
    dueDate: '2026-01-31',
    priority: 'High',
    progress: 65,
    owner: 'Cyber Risk Team',
    description: 'New guidance on mandatory cyber incident reporting',
    milestones: [
      { name: 'Draft development', date: '2025-06-01', completed: true },
      { name: 'Internal review', date: '2025-09-15', completed: true },
      { name: 'Public consultation', date: '2025-12-01', completed: false },
      { name: 'Final publication', date: '2026-01-31', completed: false },
    ],
  },
  {
    id: 'GI-004',
    code: 'CAR',
    title: 'Capital Adequacy Requirements 2026',
    type: 'Framework',
    status: 'On Track',
    dueDate: '2026-04-01',
    priority: 'High',
    progress: 45,
    owner: 'Capital Team',
    description: 'Basel III final reforms implementation',
    milestones: [
      { name: 'Preliminary draft', date: '2025-03-01', completed: true },
      { name: 'Industry consultation', date: '2025-10-15', completed: false },
      { name: 'Final publication', date: '2026-01-15', completed: false },
      { name: 'Effective date', date: '2026-04-01', completed: false },
    ],
  },
  {
    id: 'GI-005',
    code: 'B-13',
    title: 'Technology Risk Management Clarification',
    type: 'Letter',
    status: 'Behind',
    dueDate: '2025-10-30',
    priority: 'Medium',
    progress: 30,
    owner: 'Technology Risk Team',
    description: 'Clarification letter on cloud computing requirements',
    milestones: [
      { name: 'Initial drafting', date: '2025-07-01', completed: true },
      { name: 'Stakeholder input', date: '2025-09-01', completed: false },
      { name: 'Final publication', date: '2025-10-30', completed: false },
    ],
  },
  {
    id: 'GI-006',
    code: 'LICAT',
    title: 'Life Insurance Capital Adequacy Test Update',
    type: 'Guideline',
    status: 'Blocked',
    dueDate: '2026-06-01',
    priority: 'High',
    progress: 20,
    owner: 'Insurance Capital Team',
    description: 'Updates to LICAT framework pending international standards',
    milestones: [
      { name: 'Research phase', date: '2025-04-01', completed: true },
      { name: 'Draft development', date: '2025-11-01', completed: false },
      { name: 'Consultation', date: '2026-02-01', completed: false },
      { name: 'Final publication', date: '2026-06-01', completed: false },
    ],
  },
];

export default function GuidancePage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  const filteredInitiatives = guidanceInitiatives.filter((initiative) => {
    if (statusFilter !== 'all' && initiative.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && initiative.priority !== priorityFilter) return false;
    return true;
  });

  const stats = {
    total: guidanceInitiatives.length,
    onTrack: guidanceInitiatives.filter((i) => i.status === 'On Track').length,
    behind: guidanceInitiatives.filter((i) => i.status === 'Behind').length,
    blocked: guidanceInitiatives.filter((i) => i.status === 'Blocked').length,
    published: guidanceInitiatives.filter((i) => i.status === 'Published').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-sm text-gray-600">Strategic Initiatives Pipeline</p>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
              {stats.onTrack} On Track
            </span>
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
              {stats.behind} Behind Schedule
            </span>
            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
              {stats.blocked} Blocked
            </span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
              {stats.published} Published YTD
            </span>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            >
              <option value="all">All Status</option>
              <option value="On Track">On Track</option>
              <option value="Behind">Behind</option>
              <option value="Blocked">Blocked</option>
              <option value="Published">Published</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            >
              <option value="all">All Priority</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Total Initiatives" value={stats.total} icon="📋" color="blue" />
        <StatCard title="On Track" value={stats.onTrack} icon="✓" color="green" />
        <StatCard title="Behind" value={stats.behind} icon="⚠️" color="yellow" />
        <StatCard title="Blocked" value={stats.blocked} icon="🚫" color="red" />
      </div>

      {/* Initiatives List */}
      <div className="space-y-4">
        {filteredInitiatives.map((initiative) => (
          <InitiativeCard key={initiative.id} initiative={initiative} />
        ))}
      </div>

      {/* Quarterly Release Schedule */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quarterly Release Schedule</h3>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="space-y-3">
            <QuarterItem
              quarter="Q1 2025"
              date="March 2025"
              items={['E-23 Model Risk (Revised)', 'B-15 Climate Risk (Updated)']}
              status="Completed"
            />
            <QuarterItem
              quarter="Q2 2025"
              date="June 2025"
              items={['B-10 Third-Party Risk (Amendments)', 'ICAAP Updates']}
              status="Upcoming"
            />
            <QuarterItem
              quarter="Q3 2025"
              date="September 2025"
              items={['B-20 Cyber Incident Reporting (Draft)', 'Liquidity Framework Updates']}
              status="Planned"
            />
            <QuarterItem
              quarter="Q4 2025"
              date="December 2025"
              items={['CAR 2026 (Final)', 'Technology Risk Clarifications']}
              status="Planned"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }: any) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-600 uppercase">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`text-3xl p-3 rounded-lg ${colors[color as keyof typeof colors]}`}>{icon}</div>
      </div>
    </div>
  );
}

function InitiativeCard({ initiative }: { initiative: GuidanceInitiative }) {
  const [expanded, setExpanded] = useState(false);

  const statusColors = {
    'On Track': 'bg-green-100 text-green-800 border-green-300',
    Behind: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    Blocked: 'bg-red-100 text-red-800 border-red-300',
    Published: 'bg-blue-100 text-blue-800 border-blue-300',
  };

  const priorityColors = {
    High: 'text-red-600',
    Medium: 'text-yellow-600',
    Low: 'text-gray-600',
  };

  return (
    <div className={`bg-white shadow-sm rounded-lg border-2 ${statusColors[initiative.status].split(' ')[2]} overflow-hidden`}>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <span className="font-mono font-bold text-blue-700">{initiative.code}</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[initiative.status]}`}>
                {initiative.status}
              </span>
              <span className={`text-xs font-semibold ${priorityColors[initiative.priority]}`}>
                {initiative.priority} Priority
              </span>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{initiative.title}</h4>
            <p className="text-sm text-gray-600 mb-3">{initiative.description}</p>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500">Type</p>
                <p className="text-sm font-semibold text-gray-900">{initiative.type}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Due Date</p>
                <p className="text-sm font-semibold text-gray-900">{initiative.dueDate}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Owner</p>
                <p className="text-sm font-semibold text-gray-900">{initiative.owner}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Progress</span>
                <span className="text-xs font-bold text-blue-700">{initiative.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-700 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${initiative.progress}%` }}
                ></div>
              </div>
            </div>

            {/* Milestones */}
            {expanded && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Milestones</h5>
                <div className="space-y-2">
                  {initiative.milestones.map((milestone, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <div
                        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          milestone.completed
                            ? 'bg-green-500 border-green-500'
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        {milestone.completed && <span className="text-white text-xs">✓</span>}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-sm ${
                              milestone.completed ? 'text-gray-900 font-medium' : 'text-gray-600'
                            }`}
                          >
                            {milestone.name}
                          </span>
                          <span className="text-xs text-gray-500">{milestone.date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex space-x-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="px-4 py-2 bg-blue-700 text-white rounded text-sm font-medium hover:bg-blue-800"
          >
            {expanded ? 'Hide' : 'Show'} Milestones
          </button>
        </div>
      </div>
    </div>
  );
}

function QuarterItem({ quarter, date, items, status }: any) {
  const statusColors: Record<string, string> = {
    Completed: 'bg-green-50 border-green-300',
    Upcoming: 'bg-blue-50 border-blue-300',
    Planned: 'bg-gray-50 border-gray-300',
  };

  return (
    <div className={`p-4 rounded border-2 ${statusColors[status] || statusColors.Planned}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-semibold text-gray-900">{quarter}</h4>
          <p className="text-xs text-gray-600">{date}</p>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${
            status === 'Completed'
              ? 'bg-green-100 text-green-800'
              : status === 'Upcoming'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-200 text-gray-700'
          }`}
        >
          {status}
        </span>
      </div>
      <ul className="space-y-1">
        {items.map((item: string, idx: number) => (
          <li key={idx} className="text-sm text-gray-700 flex items-start">
            <span className="mr-2">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
